import { Injectable } from '@angular/core';
import { SbDataService } from '@services';
import { SbHost, SbPath } from '@models';
import { HttpParams } from '@angular/common/http';
import { EMPTY, of } from 'rxjs';
import { catchError, first, tap } from 'rxjs/operators';
import { LocationStrategy } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService {
    constructor(
        private dataService: SbDataService,
        private locationStrategy: LocationStrategy
    ) {}

    /**
     * Returns text's Base64 encoded representation.
     * @param text
     * @private
     */
    private static encode(text: string): string {
        return btoa(encodeURIComponent(text));
    }

    /**
     * Takes a Base64 encoded text string and returns its decoded representation.
     * @param text
     * @private
     */
    private static decode(text: string): string {
        return decodeURIComponent(atob(text));
    }

    /**
     * Constructs a unique key based on the given parameters
     * @param params
     * @private
     */
    private getLocalStorageKey(params: LocalStorageParams): string {
        const resourceUrl = this.dataService.getResourceUrl(
            params.path,
            params.defaultBasePath
        );

        const baseHref = this.locationStrategy.getBaseHref();
        return `${params.userName}|${baseHref}|${resourceUrl}|${params.httpParams}`;
    }

    /**
     * Retrieves cached data from local storage using the given parameters
     * and updates the loading flag.
     * Returns an observable of the cached data
     * or an EMPTY observable if no cached data is found.
     * @param params
     * @param flags
     */
    public getLocalStorageCache(
        params: LocalStorageParams,
        flags: { isLoading: boolean; lastSynchronization: Date }
    ) {
        const key = LocalStorageService.encode(this.getLocalStorageKey(params));
        const storedItem = localStorage.getItem(key);
        if (storedItem) {
            const cacheData = JSON.parse(LocalStorageService.decode(storedItem));
            if (cacheData) {
                flags.isLoading = false;
                flags.lastSynchronization = new Date(cacheData.timestamp);
                return of(cacheData.data);
            }
        }
        flags.isLoading = false;
        return EMPTY;
    }

    /**
     * Retrieves live data from a remote source using the given parameters
     * and updates the synchronization flag.
     * The retrieved data is encoded and stored in local storage.
     * Returns an observable of the retrieved data
     * or handles errors by returning an EMPTY observable.
     * @param params
     * @param flags
     */
    public getLiveData(
        params: LocalStorageParams,
        flags: { isSynchronizing: boolean; lastSynchronization: Date }
    ) {
        const key = LocalStorageService.encode(this.getLocalStorageKey(params));

        return this.dataService
            .getDataWithParams(params.path, params.httpParams, params.defaultBasePath)
            .pipe(
                tap((responseData) => {
                    const encodedData = LocalStorageService.encode(
                        JSON.stringify({ data: responseData, timestamp: Date.now() })
                    );
                    localStorage.setItem(key, encodedData);
                    flags.isSynchronizing = false;
                    flags.lastSynchronization = new Date();
                }),
                catchError((error) => {
                    console.error('LocalStorageService', 'getLiveData', error);
                    flags.isSynchronizing = false;
                    return EMPTY;
                })
            );
    }
}

export interface LocalStorageParams {
    userName: string;
    path: SbPath;
    httpParams: HttpParams;
    defaultBasePath: SbHost;
}
